import { baseApiClient as api } from "../baseService";
export const addTagTypes = ["firms", "matters"] as const;
const injectedRtkApi = api
  .enhanceEndpoints({
    addTagTypes,
  })
  .injectEndpoints({
    endpoints: (build) => ({
      filevineContactRetrieve: build.query<
        FilevineContactRetrieveApiResponse,
        FilevineContactRetrieveApiArg
      >({
        query: (queryArg) => ({
          url: `/api/firms/${queryArg.firmId}/filevine/contacts/${queryArg.id}/`,
        }),
        providesTags: ["firms"],
      }),
      filevineContactByFilevineId: build.query<
        FilevineContactByFilevineIdApiResponse,
        FilevineContactByFilevineIdApiArg
      >({
        query: (queryArg) => ({
          url: `/api/firms/${queryArg.firmId}/filevine/contacts/by-filevine-id/${queryArg.filevineId}/`,
        }),
        providesTags: ["firms"],
      }),
      filevineContactSearch: build.query<
        FilevineContactSearchApiResponse,
        FilevineContactSearchApiArg
      >({
        query: (queryArg) => ({
          url: `/api/firms/${queryArg.firmId}/filevine/contacts/search/`,
          params: {
            field_types: queryArg.fieldTypes,
            include_archived: queryArg.includeArchived,
            limit: queryArg.limit,
            q: queryArg.q,
          },
        }),
        providesTags: ["firms"],
      }),
      filevineDocumentList: build.query<
        FilevineDocumentListApiResponse,
        FilevineDocumentListApiArg
      >({
        query: (queryArg) => ({
          url: `/api/matters/${queryArg.matterId}/filevine/documents/`,
          params: {
            folder_id: queryArg.folderId,
            include_subfolders: queryArg.includeSubfolders,
            limit: queryArg.limit,
            offset: queryArg.offset,
            q: queryArg.q,
          },
        }),
        providesTags: ["matters"],
      }),
      filevineDocumentRetrieve: build.query<
        FilevineDocumentRetrieveApiResponse,
        FilevineDocumentRetrieveApiArg
      >({
        query: (queryArg) => ({
          url: `/api/matters/${queryArg.matterId}/filevine/documents/${queryArg.id}/`,
        }),
        providesTags: ["matters"],
      }),
      filevineFolderList: build.query<
        FilevineFolderListApiResponse,
        FilevineFolderListApiArg
      >({
        query: (queryArg) => ({
          url: `/api/matters/${queryArg.matterId}/filevine/documents/folders/`,
        }),
        providesTags: ["matters"],
      }),
    }),
    overrideExisting: false,
  });
export { injectedRtkApi as enhancedApi };
export type FilevineContactRetrieveApiResponse =
  /** status 200  */ FilevineContactWithHandlesSchema;
export type FilevineContactRetrieveApiArg = {
  firmId: string;
  id: string;
};
export type FilevineContactByFilevineIdApiResponse =
  /** status 200  */ FilevineContactWithHandlesSchema;
export type FilevineContactByFilevineIdApiArg = {
  filevineId: string;
  firmId: string;
};
export type FilevineContactSearchApiResponse =
  /** status 200  */ FilevineContactSearchResponse;
export type FilevineContactSearchApiArg = {
  /** Filter to specific field types: full_name, first_name, last_name, phone, email, address */
  fieldTypes?: string[];
  firmId: string;
  /** Include archived contacts in results */
  includeArchived?: boolean;
  /** Maximum number of results (1-200) */
  limit?: number;
  /** Search query (name, phone, email, or address) */
  q: string;
};
export type FilevineDocumentListApiResponse =
  /** status 200  */ FilevineDocumentListResponse[];
export type FilevineDocumentListApiArg = {
  /** Filter to documents in a specific folder (Django UUID) */
  folderId?: string;
  /** Include documents from subfolders when filtering by folder */
  includeSubfolders?: boolean;
  /** Maximum number of results (1-500) */
  limit?: number;
  matterId: string;
  /** Pagination offset */
  offset?: number;
  /** Search documents by name (case-insensitive) */
  q?: string;
};
export type FilevineDocumentRetrieveApiResponse =
  /** status 200  */ FilevineDocumentDetailedSchema;
export type FilevineDocumentRetrieveApiArg = {
  id: string;
  matterId: string;
};
export type FilevineFolderListApiResponse =
  /** status 200  */ FilevineFolderTreeResponse;
export type FilevineFolderListApiArg = {
  matterId: string;
};
export type FilevineContactPhoneSchema = {
  id: string | string;
  filevine_id: number;
  contact_id: string | string;
  phone_number: string;
  label: string | null;
  notes: string | null;
  data: {
    [key: string]: any;
  };
  created_at: string;
  modified_at: string;
};
export type FilevineContactEmailSchema = {
  id: string | string;
  filevine_id: number;
  contact_id: string | string;
  email: string;
  label: string | null;
  notes: string | null;
  data: {
    [key: string]: any;
  };
  created_at: string;
  modified_at: string;
};
export type FilevineContactAddressSchema = {
  id: string | string;
  filevine_id: number;
  contact_id: string | string;
  line1: string | null;
  line2: string | null;
  line3: string | null;
  city: string | null;
  state: string | null;
  zip: string | null;
  country: string | null;
  label: string | null;
  notes: string | null;
  data: {
    [key: string]: any;
  };
  created_at: string;
  modified_at: string;
};
export type FilevineContactWithHandlesSchema = {
  id: string | string;
  filevine_id: number;
  filevine_organization_id: string | string;
  first_name: string;
  middle_name: string | null;
  last_name: string | null;
  full_name: string;
  birth_date: string | null;
  is_archived: boolean;
  data: {
    [key: string]: any;
  };
  created_at: string;
  modified_at: string;
  phones?: FilevineContactPhoneSchema[];
  emails?: FilevineContactEmailSchema[];
  addresses?: FilevineContactAddressSchema[];
};
export type FilevineContactSchema = {
  id: string | string;
  filevine_id: number;
  filevine_organization_id: string | string;
  first_name: string;
  middle_name: string | null;
  last_name: string | null;
  full_name: string;
  birth_date: string | null;
  is_archived: boolean;
  data: {
    [key: string]: any;
  };
  created_at: string;
  modified_at: string;
};
export type FilevineContactSearchFieldType =
  | "full_name"
  | "first_name"
  | "last_name"
  | "phone"
  | "email"
  | "address";
export type FilevineContactMatchMetadata = {
  matched_field: FilevineContactSearchFieldType;
  matched_value: string;
  phone_handle_id?: (string | string) | null;
  email_handle_id?: (string | string) | null;
  address_handle_id?: (string | string) | null;
};
export type FilevineContactSearchResult = {
  contact: FilevineContactSchema;
  match: FilevineContactMatchMetadata;
  /** Trigram similarity score (1.0 = exact match) */
  similarity_score: number;
};
export type FilevineContactSearchResponse = {
  results: FilevineContactSearchResult[];
  query: string;
  organization_id: string | string;
  total_matches: number;
};
export type FilevineDocumentSchema = {
  id: string | string;
  project_id: (string | string) | null;
  filevine_id: number;
  name: string;
  deleted_at: string | null;
  created_at: string;
  modified_at: string;
};
export type FilevineDocumentVersionSchema = {
  id: string | string;
  filevine_document_id: string | string;
  revision: number;
  file_hash: string;
  file_extension: string;
  file_mime_type: string;
  file_size: number;
  document: FilevineDocumentSchema;
};
export type FilevineDocumentDetailedSchema = {
  id: string | string;
  project_id: (string | string) | null;
  filevine_id: number;
  name: string;
  deleted_at: string | null;
  created_at: string;
  modified_at: string;
  versions: FilevineDocumentVersionSchema[];
};
export type FilevineDocumentListResponse = {
  documents: FilevineDocumentDetailedSchema[];
  total_count: number;
  limit: number;
  offset: number;
};
export type FolderTree = {
  orgID: number;
  projectID?: number | null;
  id: number;
  parentID?: number | null;
  name: string;
  isRemoved: boolean;
  canEdit: boolean;
  canRemove: boolean;
  hasActiveDocLinks: boolean;
  isProjectRootFolder: boolean;
  browserPath?: string | null;
  integrationType?: string | null;
  allChildrenFetched: boolean;
  isEmpty: boolean;
  isProtected: boolean;
  children: FolderTree[];
  parents?: FolderTree[] | null;
};
export type FilevineFolderSchema = {
  id: string | string;
  project_id: string | string;
  filevine_id: number;
  name: string;
  parent_id: (string | string) | null;
  deleted_at: string | null;
  data: FolderTree;
  created_at: string;
  modified_at: string;
};
export type FilevineFolderTreeResponse = {
  folders: FilevineFolderSchema[];
  matter_id: string | string;
};
export const {
  useFilevineContactRetrieveQuery,
  useFilevineContactByFilevineIdQuery,
  useFilevineContactSearchQuery,
  useFilevineDocumentListQuery,
  useFilevineDocumentRetrieveQuery,
  useFilevineFolderListQuery,
} = injectedRtkApi;
